import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider, useAuth } from '@/lib/auth';
import { SiteHeader } from '@/components/SiteHeader';

import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { SearchResultsPage } from '@/pages/SearchResultsPage';
import { FlightDetailPage } from '@/pages/FlightDetailPage';
import { PassengersPage } from '@/pages/PassengersPage';
import { SeatMapPage } from '@/pages/SeatMapPage';
import { PaymentPage } from '@/pages/PaymentPage';
import { ConfirmationPage } from '@/pages/ConfirmationPage';
import { HotelsPage } from '@/pages/HotelsPage';
import { HotelDetailPage } from '@/pages/HotelDetailPage';
import { AIPage } from '@/pages/AIPage';
import { AccountPage } from '@/pages/AccountPage';
import { TripsPage } from '@/pages/TripsPage';
import { ETicketPage } from '@/pages/ETicketPage';

/**
 * Layout wrapper that mounts the global <SiteHeader /> above any page. Skipped on
 * `/login` and `/onboarding` (those pages have their own immersive chrome). Pages
 * that previously imported <SiteHeader /> directly should NOT do so anymore —
 * they receive it from this wrapper.
 */
function WithNav({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SiteHeader />
      {children}
    </>
  );
}

/** Guard: redirect to /login if not authenticated. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === 'unknown') return null; // still bootstrapping
  if (status === 'anon') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Guard: redirect home if already authenticated. */
function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === 'unknown') return null;
  if (status === 'authed') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route
        path="/login"
        element={
          <RedirectIfAuthed>
            <LoginPage />
          </RedirectIfAuthed>
        }
      />

      {/* Public — accessible to everyone, all wrapped with the global navbar */}
      <Route path="/" element={<WithNav><HomePage /></WithNav>} />
      <Route path="/search/:id" element={<WithNav><SearchResultsPage /></WithNav>} />
      <Route path="/flights/:offerId" element={<WithNav><FlightDetailPage /></WithNav>} />
      <Route path="/hotels" element={<WithNav><HotelsPage /></WithNav>} />
      <Route path="/hotels/:hotelId" element={<WithNav><HotelDetailPage /></WithNav>} />
      <Route path="/ai" element={<WithNav><AIPage /></WithNav>} />
      <Route path="/confirm" element={<WithNav><ConfirmationPage /></WithNav>} />

      {/* Auth-gated — also wrapped with the global navbar */}
      <Route
        path="/passengers"
        element={
          <RequireAuth>
            <WithNav><PassengersPage /></WithNav>
          </RequireAuth>
        }
      />
      <Route
        path="/seats"
        element={
          <RequireAuth>
            <WithNav><SeatMapPage /></WithNav>
          </RequireAuth>
        }
      />
      <Route
        path="/payment"
        element={
          <RequireAuth>
            <WithNav><PaymentPage /></WithNav>
          </RequireAuth>
        }
      />
      <Route
        path="/account"
        element={
          <RequireAuth>
            <WithNav><AccountPage /></WithNav>
          </RequireAuth>
        }
      />
      <Route
        path="/trips"
        element={
          <RequireAuth>
            <WithNav><TripsPage /></WithNav>
          </RequireAuth>
        }
      />
      <Route
        path="/e-ticket/:pnr"
        element={
          <RequireAuth>
            <WithNav><ETicketPage /></WithNav>
          </RequireAuth>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppRoutes />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#131C30',
                color: '#F4F3F0',
                border: '1px solid rgba(255,255,255,0.10)',
                fontFamily: '"Inter Variable","Inter",system-ui,sans-serif',
                fontSize: '14px',
              },
            }}
          />
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
